import { useEffect } from 'react';
import type { RefObject } from 'react';
import { useMediaQuery } from './use-media-query';
import { isInteractiveTarget } from '../utils/is-interactive-target.util';

/**
 * Keeps touch background taps from dismissing an already-focused input or surface.
 * Cancels only compatibility mouse focus; touch scrolling and controls stay native.
 * @param focusRef - Input or surface containing the focus to retain.
 * @param boundaryRef - Optional consuming surface that opts into retention.
 * @param enabled - Whether the input or surface is available for interaction.
 * @returns Nothing; never focuses an editor or changes native keyboard policy.
 */
export const useBackgroundFocusRetention = (
  focusRef: RefObject<HTMLElement>,
  boundaryRef: RefObject<HTMLElement> | undefined,
  enabled: boolean
) => {
  const touch = useMediaQuery('(pointer: coarse)');
  useEffect(() => {
    const surface = focusRef.current;
    const boundary = boundaryRef?.current;
    if (!enabled || !touch || !surface || !boundary) return;
    const retain = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.detail > 1 ||
        !surface.contains(surface.ownerDocument.activeElement) ||
        !(event.target instanceof Element) ||
        isInteractiveTarget(event.target)
      ) {
        return;
      }
      event.preventDefault();
    };
    boundary.addEventListener('mousedown', retain);
    return () => boundary.removeEventListener('mousedown', retain);
  }, [focusRef, boundaryRef, enabled, touch]);
};
